import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Download, Gift, MessageCircle } from "lucide-react";
import { LOGO_URL } from "../config";
import { useSite, waLink, resolveMedia } from "../siteContext";
import { useLang, pick } from "../i18n";
import { deriveFromAccent, deriveFromBg } from "../colorUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-luxury-surface border border-luxury-border text-luxury-bone px-4 py-3.5 rounded-xl focus:outline-none focus:border-luxury-gold";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
});

export default function PublicPage() {
  const { slug } = useParams();
  const settings = useSite();
  const { lang } = useLang();
  const ar = lang === "ar";
  const [page, setPage] = useState(undefined);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [sending, setSending] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/pages/${slug}`)
      .then((res) => setPage(res.data))
      .catch(() => setPage(null));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(ar ? "يرجى إدخال الاسم والبريد" : "Name and email are required");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/leads`, { ...form, source: slug, origin_url: window.location.origin });
      if (res.data.download_url) setDownloadUrl(res.data.download_url);
      toast.success(ar ? "تم التسجيل بنجاح! تفقّد/تفقّدي بريدك" : "Registered! Check your inbox");
      setForm({ name: "", email: "", phone: "" });
    } catch {
      toast.error(ar ? "حدث خطأ، حاول/حاولي مجدداً" : "Something went wrong");
    } finally {
      setSending(false);
    }
  };

  if (page === undefined) {
    return <div className="min-h-screen bg-luxury-black flex items-center justify-center"><Loader2 className="w-8 h-8 text-luxury-gold animate-spin" /></div>;
  }
  if (!page) {
    return (
      <div className="min-h-screen bg-luxury-black flex items-center justify-center px-4" data-testid="page-not-found">
        <div className="text-center">
          <img src={LOGO_URL} alt="TheMaya" className="h-10 mx-auto mb-6" />
          <p className="text-luxury-bone font-serif text-3xl mb-6">{ar ? "الصفحة غير موجودة" : "Page not found"}</p>
          <Link to="/" className="text-luxury-gold hover:underline">{ar ? "العودة للرئيسية" : "Back home"}</Link>
        </div>
      </div>
    );
  }

  const layout = page.layout || "classic";
  const cinematic = layout === "cinematic" && !!page.bg_image;
  const effectiveLayout = layout === "split" && !page.image ? "classic" : cinematic ? "cinematic" : layout === "cinematic" ? "classic" : layout;

  // Per-page theme override (CSS variables cascade to all children)
  const pageVars = {};
  if (page.accent_color) Object.assign(pageVars, Object.fromEntries(Object.entries(deriveFromAccent(page.accent_color)).map(([k, v]) => [k === "accent" ? "--c-accent" : "--c-accent-light", v])));
  if (page.bg_color) {
    const d = deriveFromBg(page.bg_color);
    pageVars["--c-bg"] = d.bg;
    pageVars["--c-bg-alt"] = d.bg_alt;
    pageVars["--c-surface"] = d.surface;
  }

  const centered = effectiveLayout === "cinematic";

  const content = (
    <motion.div {...fadeUp()} className={centered ? "text-center" : ""}>
      {page.type && page.type !== "general" && (
        <span data-testid="page-type-badge" className="inline-block text-[11px] tracking-[0.2em] text-luxury-gold border border-luxury-gold/40 rounded-full px-4 py-1.5 mb-6">
          {{ event: ar ? "فعالية" : "Event", workshop: ar ? "ورشة عمل" : "Workshop", retreat: ar ? "ريتريت" : "Retreat", course: ar ? "دورة" : "Course" }[page.type] || ""}
        </span>
      )}
      {page.start_badge && (
        <div className="mb-5">
          <span data-testid="page-start-badge" className="inline-block bg-luxury-gold text-luxury-black text-sm font-bold rounded-full px-6 py-2.5 shadow-[0_10px_40px_rgba(212,175,55,0.25)]">
            {page.start_badge}
          </span>
        </div>
      )}
      <h1 data-testid="page-title" className={`font-serif font-bold ${centered ? "text-5xl sm:text-6xl lg:text-7xl" : "text-4xl sm:text-5xl"} leading-[1.3] text-luxury-bone mb-5`}>
        {pick(lang, page, "title")}
      </h1>
      {pick(lang, page, "subtitle") && (
        <p className={`text-luxury-gold ${centered ? "text-xl" : "text-lg"} mb-5`}>{pick(lang, page, "subtitle")}</p>
      )}
      {pick(lang, page, "body") && (
        <p className="text-luxury-gray text-base md:text-lg leading-loose whitespace-pre-line mb-8">{pick(lang, page, "body")}</p>
      )}
      {page.course_id && (
        <a data-testid="page-course-link" href="/#courses"
          className="inline-flex items-center gap-2 border border-luxury-gold/60 text-luxury-gold text-sm font-semibold px-6 py-3 rounded-full hover:bg-luxury-gold hover:text-luxury-black transition-colors mb-8">
          {ar ? "اكتشف/اكتشفي الدورة المرتبطة" : "Explore the linked course"}
        </a>
      )}
      {page.payment_link && !page.is_campaign && (
        <div>
          <a data-testid="page-pay-button" href={page.payment_link} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-10 py-4 rounded-full hover:bg-luxury-goldLight transition-colors mb-8 shadow-[0_10px_40px_rgba(212,175,55,0.25)]">
            {ar ? "احجز/احجزي مكانك الآن" : "Book your spot now"}
          </a>
        </div>
      )}
      {page.magnet_file_id && (
        <div className={`flex items-center gap-3 text-luxury-gold text-sm mb-8 border border-luxury-border rounded-full px-5 py-3 w-fit ${centered ? "mx-auto" : ""}`}>
          <Gift className="w-5 h-5" strokeWidth={1.5} />
          {ar ? "هدية مجانية فورية عند التسجيل" : "Instant free gift when you sign up"}
        </div>
      )}
      {page.is_campaign && (
        <div>
          <a href="#packages" data-testid="page-campaign-cta"
            onClick={(e) => { e.preventDefault(); document.getElementById("packages")?.scrollIntoView({ behavior: "smooth" }); }}
            className="inline-flex items-center gap-2 bg-luxury-gold text-luxury-black font-bold px-10 py-4 rounded-full hover:bg-luxury-goldLight transition-colors mb-8 shadow-[0_10px_40px_rgba(212,175,55,0.25)]">
            {ar ? "احجز مكانك الآن" : "Book your spot now"}
          </a>
        </div>
      )}
    </motion.div>
  );

  const leadForm = (showImage, glass = false) => (
    <motion.form
      data-testid="page-lead-form"
      onSubmit={submit}
      {...fadeUp(0.2)}
      className={`${glass ? "bg-luxury-surface/80 backdrop-blur-xl" : "bg-luxury-surface"} border border-luxury-border rounded-2xl p-8 md:p-10 space-y-5`}
    >
      {showImage && page.image ? (
        <img src={resolveMedia(page.image)} alt="" className="w-full h-64 md:h-80 object-cover rounded-xl mb-2" />
      ) : null}
      <div>
        <label className="block text-sm text-luxury-gray mb-2">{ar ? "الاسم" : "Name"}</label>
        <input data-testid="page-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} required />
      </div>
      <div>
        <label className="block text-sm text-luxury-gray mb-2">{ar ? "البريد الإلكتروني" : "Email"}</label>
        <input data-testid="page-email-input" type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} required />
      </div>
      <div>
        <label className="block text-sm text-luxury-gray mb-2">{ar ? "رقم الجوال (اختياري)" : "Phone (optional)"}</label>
        <input data-testid="page-phone-input" dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
      </div>
      <button data-testid="page-submit-button" type="submit" disabled={sending}
        className="w-full bg-luxury-gold text-luxury-black font-semibold py-4 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
        {sending && <Loader2 className="w-4 h-4 animate-spin" />}
        {pick(lang, page, "cta_text") || (ar ? "سجّل/سجّلي الآن" : "Sign up now")}
      </button>
      {downloadUrl && (
        <a data-testid="page-download-button" href={downloadUrl}
          className="w-full flex items-center justify-center gap-2 border border-luxury-gold text-luxury-gold py-4 rounded-full hover:bg-luxury-gold hover:text-luxury-black transition-colors">
          <Download className="w-4 h-4" strokeWidth={1.5} />
          {ar ? "تحميل هديتك الآن" : "Download your gift"}
        </a>
      )}
    </motion.form>
  );

  return (
    <div className="min-h-screen bg-luxury-black hero-vignette" style={pageVars} data-testid="public-landing-page" data-layout={effectiveLayout}>
      {page.bg_image ? (
        cinematic ? (
          <>
            <img src={resolveMedia(page.bg_image)} alt="" aria-hidden="true" className="fixed inset-0 w-full h-full object-cover opacity-40 pointer-events-none" />
            <div className="fixed inset-0 bg-luxury-black/60 pointer-events-none" />
          </>
        ) : (
          <>
            <img src={resolveMedia(page.bg_image)} alt="" aria-hidden="true" className="fixed inset-0 w-full h-full object-cover opacity-25 pointer-events-none" />
            <div className="fixed inset-0 bg-luxury-black/70 pointer-events-none" />
          </>
        )
      ) : null}
      <header className="relative border-b border-luxury-border">
        <div className="max-w-5xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
          <Link to="/"><img src={LOGO_URL} alt="TheMaya" className="h-9 w-auto" /></Link>
          <a data-testid="page-whatsapp-link" href={waLink(settings, undefined, lang)} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 text-luxury-gray hover:text-luxury-gold text-sm transition-colors">
            <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
            {ar ? "واتساب" : "WhatsApp"}
          </a>
        </div>
      </header>

      {effectiveLayout === "split" ? (
        <main className="relative max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
          <motion.div {...fadeUp(0.1)} className="relative rounded-3xl overflow-hidden border border-luxury-border min-h-[320px] lg:min-h-[560px]">
            <img data-testid="page-split-image" src={resolveMedia(page.image)} alt="" className="absolute inset-0 w-full h-full object-cover" />
          </motion.div>
          <div className="flex flex-col justify-center gap-8">
            {content}
            {leadForm(false)}
          </div>
        </main>
      ) : effectiveLayout === "cinematic" ? (
        <main className="relative max-w-3xl mx-auto px-4 md:px-8 py-20 md:py-28">
          {content}
          <div className="max-w-xl mx-auto mt-10 text-start">{leadForm(true, true)}</div>
        </main>
      ) : (
        <main className="relative max-w-5xl mx-auto px-4 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {content}
          {leadForm(true)}
        </main>
      )}

      {page.is_campaign && (
        <>
          <section id="packages" data-testid="campaign-packages" className="relative max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-20 border-t border-luxury-border">
            <h2 className="font-serif font-bold text-3xl md:text-4xl text-luxury-bone text-center mb-12 leading-snug">
              قيمة الاستثمار في <span className="gold-shimmer italic">Project Magnetic</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
              <div className="bg-luxury-surface border border-luxury-border rounded-3xl p-8 flex flex-col">
                <p className="text-luxury-gold text-sm mb-3">الباقة الأساسية — Regular Package</p>
                <p className="font-serif font-bold text-5xl text-luxury-bone mb-3" dir="ltr">550$</p>
                <p className="text-luxury-gray text-sm mb-8">إمكانية الدفع على 3 أشهر</p>
                {(page.payment_link_regular || page.payment_link) && (
                  <a data-testid="pay-regular-button" href={(page.payment_link_regular || page.payment_link || "").trim()} target="_blank" rel="noopener noreferrer"
                    className="mt-auto inline-flex justify-center border border-luxury-gold text-luxury-gold font-semibold px-6 py-3.5 rounded-full hover:bg-luxury-gold hover:text-luxury-black transition-colors">
                    احجز مكانك الآن
                  </a>
                )}
              </div>
              <div className="bg-luxury-surface/50 border border-dashed border-luxury-border rounded-3xl p-8">
                <p className="text-luxury-gold text-sm mb-3">عرض خاص للعملاء الأوفياء</p>
                <p className="font-serif font-bold text-5xl text-luxury-bone mb-3" dir="ltr">400$</p>
                <p className="text-luxury-gray text-sm leading-relaxed">سعر خاص للمشاركين السابقين والأوفياء.</p>
                <p className="text-luxury-gray/70 text-xs mt-3 leading-relaxed">العرض الخاص متاح فقط لأول 15 مشتركًا مؤهلًا — يُرسل رابطه الخاص مباشرة من فريقنا.</p>
              </div>
              <div className="bg-luxury-gold/5 border border-luxury-gold/60 rounded-3xl p-8 flex flex-col shadow-[0_20px_60px_rgba(201,164,92,0.15)]">
                <p className="text-luxury-gold text-sm font-semibold mb-3">الباقة المميزة — VIP Package</p>
                <p className="font-serif font-bold text-5xl text-luxury-bone mb-4" dir="ltr">700$</p>
                <p className="text-luxury-gray text-sm mb-4">تشمل كل ما هو موجود في البرنامج الأساسي، بالإضافة إلى:</p>
                <ul className="space-y-2.5 text-sm text-luxury-bone/90 mb-5">
                  {[
                    "جلستين خاصتين مع Maya، مدة كل جلسة 60 دقيقة",
                    "تقييم شخصي للهوية والأنماط المتكررة",
                    "خطة عمل فردية لمدة 90 يومًا",
                    "أولوية في الدعم عبر الرسائل الصوتية",
                  ].map((point) => (
                    <li key={point} className="flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-luxury-gold shrink-0 mt-1.5" />
                      {point}
                    </li>
                  ))}
                </ul>
                <p className="text-luxury-gold text-xs font-semibold mb-6">المقاعد محدودة لـ 15 مشتركًا</p>
                {page.payment_link_vip && (
                  <a data-testid="pay-vip-button" href={page.payment_link_vip} target="_blank" rel="noopener noreferrer"
                    className="mt-auto inline-flex justify-center bg-luxury-gold text-luxury-black font-bold px-6 py-3.5 rounded-full hover:bg-luxury-goldLight transition-colors shadow-[0_10px_40px_rgba(212,175,55,0.25)]">
                    احجز مكانك الآن
                  </a>
                )}
              </div>
            </div>
          </section>

          <section data-testid="campaign-alt-pay" className="relative max-w-3xl mx-auto px-4 md:px-8 pb-16 text-center">
            <h3 className="font-serif text-2xl text-luxury-bone mb-4">طرق دفع أخرى متاحة</h3>
            <p className="text-luxury-gray text-sm leading-relaxed mb-7">
              يمكنك الدفع أيضًا عبر Whish Money أو Western Union أو OMT.<br />
              للدفع بإحدى هذه الطرق، تواصل معنا مباشرة عبر واتساب وسنرسل لك تفاصيل الدفع.
            </p>
            <a data-testid="alt-pay-whatsapp-button" href={waLink(settings, "مرحبًا، أرغب بالتسجيل في Project Magnetic والدفع عبر طريقة دفع بديلة.", lang)} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 border border-luxury-gold/60 text-luxury-gold font-semibold px-8 py-4 rounded-full hover:bg-luxury-gold hover:text-luxury-black transition-colors">
              <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
              تواصل معنا عبر واتساب
            </a>
            <div className="mt-10">
              <button type="button" data-testid="campaign-final-cta"
                onClick={() => document.getElementById("packages")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center gap-2 bg-luxury-gold text-luxury-black font-bold px-10 py-4 rounded-full hover:bg-luxury-goldLight transition-colors shadow-[0_10px_40px_rgba(212,175,55,0.25)]">
                احجز مكانك الآن
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}